import { useCallback, useEffect, useRef, type KeyboardEvent, type RefObject } from "react";

/**
 * GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01 — shared
 * focus-management logic for modal surfaces.
 *
 * Sprint 18 Wave A's `Drawer` established the correct discipline
 * (initial focus into the panel, Tab cycles last → first, Shift+Tab
 * cycles first → last, Escape closes, scrim closes). The FLEET
 * customer-readiness sprint needed the same discipline for a centred
 * confirmation dialog. Rather than copy the logic into a second
 * primitive, it is extracted here so `Drawer` and `Modal` share ONE
 * implementation (AGENTS.md §10 rule #2 — one implementation per
 * pattern).
 *
 * What this module adds on top of the original Drawer behaviour:
 * **focus return**. When the surface closes, focus moves back to the
 * element that was focused when it opened (normally the trigger
 * button). Without it a keyboard user is dropped at `document.body`
 * and has to tab from the top of the page again.
 */

export const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Every focusable descendant of `root`, in DOM order. */
export function getFocusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled"),
  );
}

export interface UseFocusTrapOptions {
  /** Whether the trapping surface is currently rendered. */
  readonly open: boolean;
  /** The panel element that owns the trap. */
  readonly containerRef: RefObject<HTMLElement | null>;
  /** Called on Escape. Omit `dismissOnEscape` to keep Escape active. */
  readonly onClose: () => void;
  /**
   * When false, Escape does not close the surface. Used for surfaces
   * that must not be dismissed mid-operation (e.g. a confirmation that
   * is already submitting).
   */
  readonly dismissOnEscape?: boolean;
  /**
   * Optional element to focus first. Defaults to the first focusable
   * descendant, falling back to the panel itself.
   */
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
}

export interface UseFocusTrapResult {
  /** Attach to the panel's `onKeyDown`. */
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function useFocusTrap({
  open,
  containerRef,
  onClose,
  dismissOnEscape = true,
  initialFocusRef,
}: UseFocusTrapOptions): UseFocusTrapResult {
  // The element that had focus immediately before the surface opened.
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused =
      typeof document !== "undefined" && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    returnFocusTo.current = previouslyFocused;

    const explicit = initialFocusRef?.current ?? null;
    if (explicit) {
      explicit.focus();
    } else {
      const list = getFocusables(containerRef.current);
      if (list.length > 0) list[0]!.focus();
      else containerRef.current?.focus();
    }

    return () => {
      // Focus return. `isConnected` guards the case where the trigger
      // itself was removed by the action (e.g. a row deleted itself);
      // focusing a detached node throws nothing but achieves nothing,
      // so we simply skip it and leave focus where React put it.
      const target = returnFocusTo.current;
      returnFocusTo.current = null;
      if (target && target.isConnected) target.focus();
    };
    // `initialFocusRef` / `containerRef` are stable ref objects; only
    // the open flag should re-run the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        if (!dismissOnEscape) return;
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const list = getFocusables(containerRef.current);
      if (list.length === 0) {
        event.preventDefault();
        containerRef.current?.focus();
        return;
      }
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = typeof document !== "undefined" ? document.activeElement : null;
      if (event.shiftKey) {
        if (active === first || active === containerRef.current) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [containerRef, dismissOnEscape, onClose],
  );

  return { onKeyDown };
}
